// frontend/src/pages/Help.js
// In-app help center — landing on /help, doc viewer on /help/<slug>.

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../App';
import { marked } from 'marked';

// Rewrite .md links → /help/<slug>; rewrite relative image paths → /api/help/image/<rest>.
const renderer = new marked.Renderer();
const origLink  = renderer.link.bind(renderer);
const origImage = renderer.image.bind(renderer);
renderer.link = (token) => {
  let { href = '', title, text, tokens } = token;
  if (href.endsWith('.md')) {
    const slug = href.replace(/^features\//, '').replace(/\.md$/, '');
    href = `/help/${slug}`;
  }
  return origLink({ href, title, text, tokens });
};
renderer.image = (token) => {
  let { href = '', title, text } = token;
  if (href && !/^(https?:|\/api\/)/.test(href)) {
    const cleaned = href.replace(/^\.?\//, '').replace(/^images\//, '');
    href = `/api/help/image/${cleaned}`;
  }
  return origImage({ href, title, text });
};
marked.use({ renderer, gfm: true, breaks: false });

// One-line summaries used in topic cards. Falls back to title if missing.
const SUMMARIES = {
  'getting-started':  'What the app does, supported channels, and the big idea in 5 steps.',
  'daily-workflow':   'A typical operator\'s day — screen by screen, in order.',
  'dashboard':        'Read overnight revenue, alerts, and top movers at a glance.',
  'orders':           'Filter, pick, ship, and track orders across every channel.',
  'inventory':        'Manage products, quantities, and channel listings.',
  'pick-list':        'Bin-sorted picking workflow — desktop and mobile.',
  'messages':         'Unified buyer inbox with reply templates and one-click restock.',
  'bin-report':       'Per-bin inventory view with AI-powered relist for stale items.',
  'archive':          'Ended/closed listings — search and one-click relist.',
  'manual-orders':    'Enter Poshmark / Mercari / Facebook sales by hand.',
  'sync-logs':        'History of every sync run — first place to look when something seems off.',
  'reconciliation':   'Weekly audit of master vs. each channel — surfaces drift.',
  'drift':            'Daily oversell-risk view — Δ between master and channel.',
  'reports':          'Sales analytics, stale listings, and duplicate-UPC checks.',
};

export default function Help() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [doc, setDoc]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');

  useEffect(() => {
    api.get('/help/list')
      .then(r => setPages(r.data.pages))
      .catch(() => setListError('Failed to load help index'));
  }, []);

  useEffect(() => {
    if (!slug) { setDoc(null); return; }
    setLoading(true);
    api.get(`/help/page/${encodeURIComponent(slug)}`).then(r => {
      setDoc(r.data); setLoading(false);
    }).catch(() => { setDoc(null); setLoading(false); });
  }, [slug]);

  const grouped = useMemo(() => {
    const out = {};
    for (const p of pages) (out[p.group] = out[p.group] || []).push(p);
    return out;
  }, [pages]);

  const html = useMemo(() => {
    if (!doc?.content) return '';
    try { return marked.parse(doc.content); } catch { return '<p>Failed to render.</p>'; }
  }, [doc]);

  function handleContentClick(e) {
    const a = e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (href.startsWith('/help/')) { e.preventDefault(); navigate(href); }
  }

  // ── LANDING ────────────────────────────────────────────────────────────────
  if (!slug) {
    return (
      <div className="help-landing">
        <header className="help-hero">
          <div className="help-hero-icon">📖</div>
          <h1>Help center</h1>
          <p>Get the most out of Euphoric Sync — overviews, walkthroughs, and a per-feature reference.</p>
        </header>

        <section className="help-cards">
          <Link to="/help/getting-started" className="help-card help-card-primary">
            <div className="help-card-emoji">🚀</div>
            <div className="help-card-title">First time here?</div>
            <div className="help-card-desc">A 2-minute overview of what Euphoric Sync does and the channels it covers.</div>
            <div className="help-card-cta">Start the tour →</div>
          </Link>

          <Link to="/help/getting-started" className="help-card">
            <div className="help-card-emoji">📅</div>
            <div className="help-card-title">A day in the life</div>
            <div className="help-card-desc">The typical operator's daily flow — open Dashboard, work the pick list, ship, reply, report.</div>
            <div className="help-card-cta">See the workflow →</div>
          </Link>

          <a href="#all-topics" className="help-card">
            <div className="help-card-emoji">📚</div>
            <div className="help-card-title">Browse by feature</div>
            <div className="help-card-desc">Jump straight to detailed guides for each page in the app.</div>
            <div className="help-card-cta">Browse topics →</div>
          </a>
        </section>

        <section id="all-topics" className="help-topics">
          <div className="help-topics-heading">All topics</div>
          {listError && <div className="help-error">{listError}</div>}
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group} className="help-topic-group">
              <div className="help-topic-group-label">{group}</div>
              <div className="help-topic-grid">
                {items.map(p => (
                  <Link key={p.slug} to={`/help/${p.slug}`} className="help-topic-row">
                    <div className="help-topic-row-title">{p.title}</div>
                    <div className="help-topic-row-desc">
                      {SUMMARIES[p.slug] || 'Open to read more.'}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </section>
      </div>
    );
  }

  // ── DOC VIEWER ─────────────────────────────────────────────────────────────
  return (
    <div className="help-page" style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
      <aside className="help-sidebar">
        <Link to="/help" className="help-sidebar-home">← Help center</Link>
        {Object.entries(grouped).map(([group, items]) => (
          <div key={group} className="help-sidebar-group">
            <div className="help-sidebar-group-label">{group}</div>
            {items.map(p => (
              <Link key={p.slug} to={`/help/${p.slug}`}
                className={`help-sidebar-link ${slug === p.slug ? 'active' : ''}`}>
                {p.title}
              </Link>
            ))}
          </div>
        ))}
      </aside>

      <main style={{ flex: 1, minWidth: 0, maxWidth: 820 }}>
        {loading ? (
          <div className="help-empty">Loading…</div>
        ) : !doc ? (
          <div className="help-empty">Page not found. <Link to="/help">Back to help center</Link></div>
        ) : (
          <>
            <div className="help-breadcrumb">
              <Link to="/help">Help</Link> <span>›</span> <span>{doc.group}</span> <span>›</span> <span>{doc.title}</span>
            </div>
            <div className="card help-content"
              onClick={handleContentClick}
              dangerouslySetInnerHTML={{ __html: html }} />
          </>
        )}
      </main>
    </div>
  );
}
